/*
 * Size calculator for the product page.
 *
 * Rules are transcribed literally from the brand's size.rtf / undersize.rtf /
 * oversize.rtf algorithms. Each rule is [minHeight, maxHeight, minWeight,
 * maxWeight, size] with both bounds inclusive, and — exactly like the chain of
 * `if` statements in the source — the LAST matching rule wins.
 *
 * `normal`    — product runs true to size
 * `undersize` — product runs small, so every result moves one size up
 * `oversize`  — product runs large, so every result moves one size down
 */

const SIZE_RULES = {
  normal: {
    height: [130, 202],
    weight: [35, 135],
    rules: [
      [150, 202, 0, 48, 'XXS'],
      [150, 175, 48, 51, 'XXS'],
      [130, 155, 35, 54, 'XXS'],

      [175, 202, 48, 51, 'XS'],
      [155, 202, 51, 54, 'XS'],
      [150, 187, 54, 57, 'XS'],
      [150, 180, 57, 60, 'XS'],

      [187, 202, 54, 57, 'S'],
      [180, 202, 57, 60, 'S'],
      [150, 202, 60, 63, 'S'],
      [150, 185, 63, 66, 'S'],
      [150, 170, 66, 69, 'S'],

      [185, 202, 63, 66, 'M'],
      [170, 202, 66, 69, 'M'],
      [150, 202, 69, 72, 'M'],
      [150, 190, 72, 75, 'M'],
      [150, 175, 75, 78, 'M'],

      [190, 202, 72, 75, 'L'],
      [175, 202, 75, 78, 'L'],
      [150, 202, 78, 81, 'L'],
      [150, 187, 81, 84, 'L'],
      [150, 180, 84, 87, 'L'],

      [187, 202, 81, 84, 'XL'],
      [180, 202, 84, 87, 'XL'],
      [150, 202, 87, 90, 'XL'],
      [150, 195, 90, 93, 'XL'],
      [150, 190, 93, 96, 'XL'],

      [195, 202, 90, 93, 'XXL'],
      [190, 202, 93, 96, 'XXL'],
      [150, 202, 96, 102, 'XXL'],
      [150, 180, 102, 110, 'XXL'],

      [180, 202, 102, 110, 'XXXL'],
      [150, 202, 110, 115, 'XXXL'],
      [150, 185, 115, 120, 'XXXL'],
      [150, 170, 120, 125, 'XXXL'],

      [185, 202, 115, 120, '4XL'],
      [170, 202, 120, 125, '4XL'],
      [150, 202, 125, 135, '4XL'],
    ],
  },

  undersize: {
    height: [130, 202],
    weight: [35, 120],
    rules: [
      [150, 202, 0, 48, 'XS'],
      [150, 175, 48, 51, 'XS'],
      [130, 155, 35, 54, 'XS'],

      [175, 202, 48, 51, 'S'],
      [155, 202, 51, 54, 'S'],
      [150, 187, 54, 57, 'S'],
      [150, 180, 57, 60, 'S'],

      [187, 202, 54, 57, 'M'],
      [180, 202, 57, 60, 'M'],
      [150, 202, 60, 63, 'M'],
      [150, 185, 63, 66, 'M'],
      [150, 170, 66, 69, 'M'],

      [185, 202, 63, 66, 'L'],
      [170, 202, 66, 69, 'L'],
      [150, 202, 69, 72, 'L'],
      [150, 190, 72, 75, 'L'],
      [150, 175, 75, 78, 'L'],

      [190, 202, 72, 75, 'XL'],
      [175, 202, 75, 78, 'XL'],
      [150, 202, 78, 81, 'XL'],
      [150, 187, 81, 84, 'XL'],
      [150, 180, 84, 87, 'XL'],

      [187, 202, 81, 84, 'XXL'],
      [180, 202, 84, 87, 'XXL'],
      [150, 202, 87, 90, 'XXL'],
      [150, 195, 90, 93, 'XXL'],
      [150, 190, 93, 96, 'XXL'],

      [195, 202, 90, 93, 'XXXL'],
      [190, 202, 93, 96, 'XXXL'],
      [150, 202, 96, 102, 'XXXL'],
      [150, 180, 102, 110, 'XXXL'],

      [180, 202, 102, 110, '4XL'],
      [150, 202, 110, 120, '4XL'],
    ],
  },

  oversize: {
    height: [130, 202],
    weight: [35, 145],
    rules: [
      [150, 202, 0, 48, 'XXXS'],
      [150, 175, 48, 51, 'XXXS'],
      [130, 155, 35, 54, 'XXXS'],

      [175, 202, 48, 51, 'XXS'],
      [155, 202, 51, 54, 'XXS'],
      [150, 187, 54, 57, 'XXS'],
      [150, 180, 57, 60, 'XXS'],

      [187, 202, 54, 57, 'XS'],
      [180, 202, 57, 60, 'XS'],
      [150, 202, 60, 63, 'XS'],
      [150, 185, 63, 66, 'XS'],
      [150, 170, 66, 69, 'XS'],

      [185, 202, 63, 66, 'S'],
      [170, 202, 66, 69, 'S'],
      [150, 202, 69, 72, 'S'],
      [150, 190, 72, 75, 'S'],
      [150, 175, 75, 78, 'S'],

      [190, 202, 72, 75, 'M'],
      [175, 202, 75, 78, 'M'],
      [150, 202, 78, 81, 'M'],
      [150, 187, 81, 84, 'M'],
      [150, 180, 84, 87, 'M'],

      [187, 202, 81, 84, 'L'],
      [180, 202, 84, 87, 'L'],
      [150, 202, 87, 90, 'L'],
      [150, 195, 90, 93, 'L'],
      [150, 190, 93, 96, 'L'],

      [195, 202, 90, 93, 'XL'],
      [190, 202, 93, 96, 'XL'],
      [150, 202, 96, 102, 'XL'],
      [150, 180, 102, 110, 'XL'],

      [180, 202, 102, 110, 'XXL'],
      [150, 202, 110, 115, 'XXL'],
      [150, 185, 115, 120, 'XXL'],
      [150, 170, 120, 125, 'XXL'],

      [185, 202, 115, 120, 'XXXL'],
      [170, 202, 120, 125, 'XXXL'],
      [150, 202, 125, 130, 'XXXL'],
      [150, 170, 130, 135, 'XXXL'],

      [170, 202, 130, 135, '4XL'],
      [150, 202, 135, 145, '4XL'],
    ],
  },
};

// Size labels as they may appear in a product's option values.
const SIZE_ALIASES = {
  XXS: ['XXS', '2XS'],
  XS: ['XS'],
  S: ['S'],
  M: ['M'],
  L: ['L'],
  XL: ['XL'],
  XXL: ['XXL', '2XL'],
  XXXL: ['XXXL', '3XL'],
  '4XL': ['4XL', 'XXXXL'],
  XXXS: ['XXXS', '3XS'],
};

class SizeCalculator extends HTMLElement {
  connectedCallback() {
    this.fit = SIZE_RULES[this.dataset.fit] ? this.dataset.fit : 'normal';
    this.form = this.querySelector('[data-sc-form]');
    this.heightInput = this.querySelector('[data-sc-height]');
    this.weightInput = this.querySelector('[data-sc-weight]');
    this.result = this.querySelector('[data-sc-result]');
    this.resultSize = this.querySelector('[data-sc-size]');
    this.resultNote = this.querySelector('[data-sc-note]');
    this.error = this.querySelector('[data-sc-error]');
    this.apply = this.querySelector('[data-sc-apply]');

    if (!this.form) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });

    if (this.apply) {
      this.apply.addEventListener('click', () => this.selectVariant());
    }
  }

  calculate() {
    const config = SIZE_RULES[this.fit];
    const height = parseFloat(this.heightInput.value);
    const weight = parseFloat(this.weightInput.value);

    if (!Number.isFinite(height) || !Number.isFinite(weight)) {
      this.showError(this.dataset.errorEmpty);
      return;
    }

    const [minH, maxH] = config.height;
    const [minW, maxW] = config.weight;
    if (height < minH || height > maxH || weight < minW || weight > maxW) {
      this.showError(this.dataset.errorRange);
      return;
    }

    let size = null;
    for (const [hMin, hMax, wMin, wMax, value] of config.rules) {
      if (height >= hMin && height <= hMax && weight >= wMin && weight <= wMax) {
        size = value;
      }
    }

    if (!size) {
      this.showError(this.dataset.errorRange);
      return;
    }

    this.showResult(size);
  }

  showError(message) {
    if (this.result) this.result.hidden = true;
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.hidden = false;
  }

  showResult(size) {
    if (this.error) this.error.hidden = true;
    if (!this.result) return;

    this.currentSize = size;
    this.resultSize.textContent = size;
    this.result.hidden = false;

    const input = this.findOptionInput(size);
    if (this.apply) {
      this.apply.hidden = !input;
      this.apply.textContent = (this.dataset.applyLabel || '').replace('%s', size);
    }
    if (this.resultNote) {
      this.resultNote.textContent = input ? this.dataset.noteFit || '' : this.dataset.noteMissing || '';
    }
  }

  /* Find the variant radio whose value matches the calculated size.
     Radios carry form="<product_form_id>", which pins the lookup to this
     product's form and keeps quick-add widgets elsewhere on the page out. */
  findOptionInput(size) {
    const formId = this.dataset.productForm;
    if (!formId) return null;

    const wanted = (SIZE_ALIASES[size] || [size]).map((value) => value.toUpperCase());
    const inputs = document.querySelectorAll(
      `input[type="radio"][form="${CSS.escape(formId)}"]`
    );
    return (
      Array.from(inputs).find((input) => wanted.includes((input.value || '').trim().toUpperCase())) || null
    );
  }

  selectVariant() {
    const input = this.findOptionInput(this.currentSize);
    if (!input) return;

    const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    if (label) {
      label.click();
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.closest('modal-dialog')?.hide?.();
  }
}

if (!customElements.get('size-calculator')) {
  customElements.define('size-calculator', SizeCalculator);
}
